import { Controller } from "@hotwired/stimulus"
import {
  activeSidebar, isMobile, isDrillOpen, openDrillPanel, closeDrillPanel, removeDrillPanel
} from "../lib/sidebar_drill"

export default class extends Controller {
  static targets = ["toggle", "items"]

  connect() {
    if (!this.hasItemsTarget || !this.hasToggleTarget) return
    this.menuElement = this.itemsTarget
    this.toggleElement = this.toggleTarget
    this.originalParent = this.menuElement.parentNode
    this.boundClose = this.close.bind(this)
    this.boundBeforeCache = this.#beforeCache.bind(this)
    this.boundBeforeFrameRender = this.#beforeFrameRender.bind(this)
    this.boundResize = this.#onResize.bind(this)
    document.addEventListener("click", this.boundClose)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    document.addEventListener("turbo:before-frame-render", this.boundBeforeFrameRender)
    window.addEventListener("resize", this.boundResize)

    this.#prepareFlyout()
    this.#hideMenu()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    document.removeEventListener("turbo:before-frame-render", this.boundBeforeFrameRender)
    window.removeEventListener("resize", this.boundResize)
    this.#restoreMenu()
  }

  toggle(event) {
    event.preventDefault()
    if (!this.menuElement || !this.toggleElement) return

    if (isMobile()) {
      this.#toggleDrillDown()
    } else if (this.menuElement.classList.contains("show")) {
      this.#hideMenu()
    } else {
      this.#openMenu()
    }
  }

  close(event) {
    if (!this.menuElement || !this.toggleElement) return
    if (isMobile()) return
    if (!this.element.contains(event.target) && !this.menuElement.contains(event.target)) {
      this.#hideMenu()
    }
  }

  // ── Desktop flyout ──────────────────────────────────────────────────

  #openMenu() {
    this.#prepareFlyout()
    this.#positionMenu()
    this.menuElement.style.display = "block"
    this.menuElement.classList.add("show")
    this.menuElement.style.visibility = "visible"
    this.menuElement.style.opacity = "1"
    this.element.classList.add("open")
    this.toggleElement.setAttribute("aria-expanded", "true")
    this.#updateSidebarFlyoutState()
  }

  #hideMenu() {
    this.menuElement.classList.remove("show")
    this.menuElement.style.display = "none"
    this.menuElement.style.visibility = "hidden"
    this.menuElement.style.opacity = "0"
    this.element.classList.remove("open")
    if (this.toggleElement) this.toggleElement.setAttribute("aria-expanded", "false")
    this.#updateSidebarFlyoutState()
  }

  #updateSidebarFlyoutState() {
    const sidebar = this.element.closest(".gl-sidebar")
    if (!sidebar) return

    sidebar.classList.toggle("has-open-flyout", Boolean(sidebar.querySelector(".gl-sidebar-group.open")))
  }

  #positionMenu() {
    const sidebar = document.querySelector(".gl-sidebar")
    if (!sidebar) return
    const rect = this.toggleElement.getBoundingClientRect()
    const menu = this.menuElement

    menu.style.visibility = "hidden"
    menu.style.display = "block"
    const menuHeight = menu.scrollHeight
    menu.style.display = "none"

    menu.style.position = "fixed"
    const sidebarRect = sidebar.getBoundingClientRect()
    menu.style.left = `${sidebarRect.right + 4}px`

    const maxTop = Math.max(10, window.innerHeight - menuHeight - 10)
    menu.style.top = `${Math.min(Math.max(10, rect.top), maxTop)}px`
    menu.style.bottom = "auto"
  }

  #prepareFlyout() {
    if (!this.menuElement) return

    this.menuElement.classList.add("gl-sidebar-flyout")
    this.#injectTitle()

    if (this.menuElement.parentNode !== document.body) {
      document.body.appendChild(this.menuElement)
    }
  }

  #injectTitle() {
    if (this.menuElement.querySelector(".gl-sidebar-flyout-title")) return
    const label = this.toggleElement.querySelector("span")
    if (!label) return
    const title = document.createElement("div")
    title.className = "gl-sidebar-flyout-title"
    title.textContent = label.textContent
    this.menuElement.prepend(title)
  }

  #restoreMenu() {
    if (!this.menuElement || this.menuElement.parentNode !== document.body) return
    this.#hideMenu()
    const title = this.menuElement.querySelector(".gl-sidebar-flyout-title")
    if (title) title.remove()
    this.menuElement.classList.remove("gl-sidebar-flyout")
    if (this.originalParent && this.originalParent.isConnected) {
      this.originalParent.appendChild(this.menuElement)
    } else {
      this.menuElement.remove()
    }
  }

  #beforeCache() {
    if (isDrillOpen()) closeDrillPanel()
    removeDrillPanel()
    this.#restoreMenu()
  }

  // Profile/Settings tabs swap the #settings_panel Turbo Frame, which fires
  // turbo:before-frame-render but NOT turbo:before-cache/disconnect. Close any
  // open flyout so its body-detached DOM isn't stranded across the frame swap.
  // Unlike #beforeCache, the menu is only hidden (not restored to the sidebar):
  // the controller stays connected here, so the flyout must remain detached and
  // reusable for the next open.
  #beforeFrameRender() {
    if (isDrillOpen()) closeDrillPanel()
    removeDrillPanel()
    this.#hideMenu()
  }

  // ── Mobile drill-down ───────────────────────────────────────────────

  #toggleDrillDown() {
    if (isDrillOpen()) {
      closeDrillPanel()
    } else {
      this.#openDrillDown()
    }
  }

  #openDrillDown() {
    const sidebar = activeSidebar()
    if (!sidebar) return

    const label = this.toggleElement.querySelector("span")
    const title = label ? label.textContent : "Menu"

    openDrillPanel(sidebar, title, this.menuElement, ".gl-sidebar-flyout-title")

    this.element.classList.add("open")
    this.toggleElement.setAttribute("aria-expanded", "true")
  }

  #onResize() {
    if (!isMobile() && isDrillOpen()) {
      closeDrillPanel()
    }
  }
}
